import { Box, Stack, Text, useMantineTheme } from '@mantine/core'
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'

import AttributeSettings from './AttributeSettings'
import PresetValues from './PresetValues'

interface SelectionBoxesProps {
  rules: any
  options: string[]
  optionIcons?: JSX.Element[]
  properties: any
  setProperties: Dispatch<SetStateAction<{ [key: string]: any }>>
}

const SelectionBoxes: React.FC<SelectionBoxesProps> = ({
  rules,
  options,
  optionIcons,
  properties,
  setProperties,
}) => {
  const theme = useMantineTheme()
  const [selectedMovement, setSelectedMovement] = useState<number>(properties.movementType || 0)

  // get key with index = selectedMovement from rules.attributes
  const selectedMovementType = useMemo(() => {
    if (rules?.attributes) {
      return Object.keys(rules.attributes)[selectedMovement]
    }
    return ''
  }, [rules, selectedMovement])

  const handleTypeClick = useCallback(
    (option: number) => {
      // remove from properties keys that are in rules.attributes[selectedMovementType]
      // since selectedMovementType is not updated yet, we need to use the old value
      setProperties(prevProperties => {
        const newProperties = { ...prevProperties }
        Object.keys(newProperties).forEach(key => {
          if (
            rules?.attributes[selectedMovementType] &&
            Object.keys(rules?.attributes[selectedMovementType]).includes(key)
          ) {
            delete newProperties[key]
          }
        })
        return newProperties
      })
      setSelectedMovement(option)
    },
    [rules?.attributes, selectedMovementType, setProperties, setSelectedMovement]
  )

  const typePicker = useMemo(
    () =>
      options.map((option, index) => (
        <Box
          key={option}
          style={{
            display: 'flex',
            borderRadius: '8px',
            width: '90px',
            height: '90px',
            backgroundColor: 'transparent',
            border:
              selectedMovement === index
                ? `2px solid ${theme.colors.l1Primary[0]}`
                : `2px solid ${theme.colors.splitter[0]}`,
            position: 'relative',
            cursor: 'pointer',
            alignContent: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => handleTypeClick(index)}
        >
          <Stack
            style={{
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0px',
            }}
          >
            {optionIcons && (
              <Text
                style={{
                  color:
                    selectedMovement === index
                      ? theme.colors.l1Primary[0]
                      : theme.colors.paragraph[0],
                }}
              >
                {optionIcons[index]}
              </Text>
            )}

            <Text
              style={{
                textAlign: 'center',
                color:
                  selectedMovement === index
                    ? theme.colors.l1Primary[0]
                    : theme.colors.paragraph[0],
              }}
            >
              {option}
            </Text>
          </Stack>
        </Box>
      )),
    [selectedMovement, options, optionIcons]
  )

  return (
    <>
      <Stack style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '10px' }}>{typePicker}</Stack>
      {rules?.attributes && rules.attributes[selectedMovementType] && (
        <AttributeSettings
          values={rules.attributes[selectedMovementType]}
          properties={properties}
          setProperties={setProperties}
        />
      )}
      {rules?.presets && rules.presets[selectedMovementType] && (
        <PresetValues values={rules.presets[selectedMovementType]} setProperties={setProperties} />
      )}
    </>
  )
}

export default SelectionBoxes
